import { useCallback, useMemo } from 'react';
import type { SpassuCodeApi } from '../../api';
import type {
  Activity,
  ActivityFilterOptions,
  ActivityFilters,
  PaginatedResult,
} from '../../api/types';
import { usePortfolioRequest } from './usePortfolioRequest';

type PortfolioApi = Pick<
  SpassuCodeApi,
  'listActivities' | 'getActivityFilterOptions'
>;
const EMPTY_ACTIVITIES: Activity[] = [];
const EMPTY_OPTIONS: ActivityFilterOptions = {
  projects: [],
  repositories: [],
  creators: [],
};

function useFilterOptionsQuery(api: PortfolioApi, filters: ActivityFilters) {
  const context = useMemo(
    () => ({
      status: filters.status,
      workPath: filters.workPath,
      azureProject: filters.azureProject,
      azureRepository: filters.azureRepository,
      source: filters.source,
      issueExternalId: filters.issueExternalId,
      createdFrom: filters.createdFrom,
      createdToExclusive: filters.createdToExclusive,
      mineOnly: filters.mineOnly,
    }),
    [
      filters.status,
      filters.workPath,
      filters.azureProject,
      filters.azureRepository,
      filters.source,
      filters.issueExternalId,
      filters.createdFrom,
      filters.createdToExclusive,
      filters.mineOnly,
    ],
  );
  return useCallback(async () => {
    const result = await api.getActivityFilterOptions(context);
    return {
      projects: result.projects ?? [],
      repositories: result.repositories ?? [],
      creators: result.creators ?? [],
    };
  }, [api, context]);
}

export function usePortfolioQueries(
  api: PortfolioApi,
  filters: ActivityFilters,
  actionError?: Error,
) {
  const listQuery = useCallback(
    () => api.listActivities(filters),
    [api, filters],
  );
  const optionsQuery = useFilterOptionsQuery(api, filters);
  const list = usePortfolioRequest<PaginatedResult<Activity> | undefined>(
    listQuery,
    undefined,
  );
  const options = usePortfolioRequest(optionsQuery, EMPTY_OPTIONS);
  return {
    activities: list.data?.data ?? EMPTY_ACTIVITIES,
    total: list.data?.total ?? 0,
    loading: list.loading,
    error: actionError ?? list.error,
    loadActivities: list.refresh,
    filterOptions: options.data,
    loadingFilterOptions: options.loading,
    filterOptionsReady: options.ready,
    loadFilterOptions: options.refresh,
  };
}
